import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAdminSession } from "@/hooks/use-admin-session";
import { listEquipmentOverview } from "@/lib/equipment.functions";
import { BulkAssignDialog } from "@/components/admin/BulkAssignDialog";
import { Loader2, Laptop, KeyRound, FileClock, AlarmClock, CircleCheck } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/ausstattung/")({
  head: () => ({
    meta: [
      { title: "Ausstattung – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EquipmentOverviewPage,
});

function EquipmentOverviewPage() {
  const session = useAdminSession();
  const loadFn = useServerFn(listEquipmentOverview);
  const queryClient = useQueryClient();
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["equipment-overview"],
    queryFn: () => loadFn(),
    enabled: session.isAdmin,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Ausstattung wird geladen …
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="text-sm text-destructive">
        Ausstattung konnte nicht geladen werden.
        {error instanceof Error ? ` (${error.message})` : null}
      </div>
    );
  }

  const cards = [
    { icon: Laptop, label: "Aktive Geräte", value: data.stats.activeAssets },
    { icon: KeyRound, label: "Ausgeliehene Schlüssel", value: data.stats.keys },
    { icon: FileClock, label: "Lizenzen", value: data.stats.licenses },
    { icon: AlarmClock, label: "Läuft in 60 Tagen ab", value: data.stats.expiringLicenses },
    { icon: CircleCheck, label: "Offene Bestätigungen", value: data.stats.openConfirmations },
  ];

  return (
    <div className="max-w-5xl">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
            Ausstattung
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Geräte, Schlüssel, SIM-Karten und Lizenzen des Teams. Wählen Sie eine
            Person, um ihre Ausstattung zu pflegen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-foreground"
        >
          Gerät(e) zuweisen
        </button>
      </header>

      {bulkOpen ? (
        <BulkAssignDialog
          people={data.people}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            queryClient.invalidateQueries({ queryKey: ["equipment-overview"] });
            queryClient.invalidateQueries({ queryKey: ["equipment-person"] });
          }}
        />
      ) : null}

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map(({ icon: Icon, label, value }) => (
          <div key={label} className="rounded-lg border border-border p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Icon className="h-4 w-4" /> {label}
            </div>
            <div className="mt-2 font-display text-2xl font-extrabold text-primary">
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Person</th>
              <th className="px-4 py-3 font-semibold">Rolle</th>
              <th className="px-4 py-3 font-semibold text-right">Aktive Geräte</th>
              <th className="px-4 py-3 font-semibold text-right">Lizenzen</th>
              <th className="px-4 py-3" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {data.people.map((person) => (
              <tr key={person.id} className="border-b border-border last:border-b-0">
                <td className="px-4 py-3">
                  <div className="font-semibold">{person.name}</div>
                  {person.email ? (
                    <div className="text-xs text-muted-foreground">{person.email}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {person.role === "admin" ? "Administrator" : "Editor"}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {person.activeAssetCount}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {person.licenseCount}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to="/admin/ausstattung/$userId"
                    params={{ userId: person.id }}
                    className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-primary hover:bg-secondary"
                  >
                    Ansehen
                  </Link>
                </td>
              </tr>
            ))}
            {data.people.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Noch keine Mitarbeitenden angelegt.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
